import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Button, ButtonGroup } from "react-bootstrap";

import {
  PositionApiResponse,
  PositionUpdateRequest,
  fetchPosition,
  createPosition,
  updatePosition,
  deletePosition,
} from "../../../../api/positionApi";
import { PAGE_TITLE, OPERATION_NAME } from "../../../../constants";
import { Operation } from "../../../../types";
import { Table, TableContent } from "../..";

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 5px;
`;

const PositionList = () => {
  const [positionData, setPositionData] = useState<PositionApiResponse[]>([]);
  const [editFormData, setEditFormData] = useState<
    { positionName: string } | PositionUpdateRequest | null
  >(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [operationName, setOperationName] = useState<Operation | null>(null);

  useEffect(() => {
    fetchPosition().then((positionData) => {
      setPositionData(positionData);
    });
  }, [operationName]);

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    // NOTE: フォームのデフォルト動作を防止
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const positionName: string = formData.get("positionName") as string;

    try {
      await createPosition({ positionName });
    } catch (error) {
      alert("Create error: " + error);
    }

    closeModal();
  };

  const handleEditClick = (item: PositionUpdateRequest) => {
    openModal(OPERATION_NAME.EDIT);
    setEditFormData({
      id: item.id,
      positionName: item.positionName,
    });
  };

  const handleSave = async () => {
    if (!editFormData) return;

    try {
      await updatePosition(editFormData as PositionUpdateRequest);
    } catch (error) {
      alert("Update error: " + error);
    }

    setEditFormData(null);
    closeModal();
  };

  const handleDelete = async (id: string) => {
    setOperationName(OPERATION_NAME.DELETE);

    try {
      if (confirm("Are you sure you want to delete this item?")) await deletePosition({ id });
    } catch (error) {
      alert("Delete error: " + error);
    }

    setOperationName(null);
  };

  const openModal = (operation: Operation) => {
    setOperationName(operation);
    setShowModal(true);
  };

  const closeModal = () => {
    setOperationName(null);
    setShowModal(false);
  };

  return (
    <>
      <h2>{PAGE_TITLE.PositionListPage}</h2>
      <div className="my-3">
        <Button variant="primary" onClick={() => openModal(OPERATION_NAME.CREATE)}>
          新規作成
        </Button>
      </div>
      <TableContent>
        <Table>
          <thead>
            <tr>
              <th>Position Name</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {positionData.map((positionItem) => (
              <tr key={positionItem.id}>
                <td>{positionItem.positionName}</td>
                <td className="text-center">
                  {new Date(positionItem.createdAt).toLocaleDateString()}
                </td>
                <td className="text-center">
                  {new Date(positionItem.updatedAt).toLocaleDateString()}
                </td>
                <td className="text-center">
                  <ButtonGroup>
                    <Button variant="primary" onClick={() => handleEditClick(positionItem)}>
                      編集
                    </Button>
                    <Button variant="secondary" onClick={() => handleDelete(positionItem.id)}>
                      削除
                    </Button>
                  </ButtonGroup>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContent>
      {showModal && operationName === OPERATION_NAME.CREATE && (
        <ModalBackdrop>
          <ModalContent>
            <form onSubmit={handleCreate}>
              <input type="text" name="positionName" placeholder="Position Name" />
              <button type="submit">Save</button>
              <button type="button" onClick={closeModal}>
                Cancel
              </button>
            </form>
          </ModalContent>
        </ModalBackdrop>
      )}
      {showModal && operationName === OPERATION_NAME.EDIT && editFormData && (
        <ModalBackdrop>
          <ModalContent>
            <form onSubmit={handleSave}>
              <input
                type="text"
                name="positionName"
                placeholder="Position Name"
                value={editFormData.positionName}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    positionName: e.target.value,
                  })
                }
              />
              <button type="submit">Save</button>
              <button
                type="button"
                onClick={() => {
                  setEditFormData(null);
                  closeModal();
                }}
              >
                Cancel
              </button>
            </form>
          </ModalContent>
        </ModalBackdrop>
      )}
    </>
  );
};

export default PositionList;
